import React from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { LocaleText } from '../../../locale';
import { ECurrencySymbolDisplay, EShowPageSetting } from '../types';
import { styles } from './ExportPdfHeaderControllers.styles';

export interface IExportPdfHeaderControllersProps {
  localeText: LocaleText;
  showHeader: boolean;
  toggleShowHeader(): void;
  showPageSetting: string;
  setShowPageSetting: (showPageSetting: EShowPageSetting) => void;
  primaryTitle: string;
  setPrimaryTitle: (primaryTitle: string) => void;
  secondaryTitle: string;
  setSecondaryTitle: (secondaryTitle: string) => void;
  subtitle: string;
  setSubtitle: (subtitle: string) => void;
  showPrimaryTitle: boolean;
  toggleShowPrimaryTitle(): void;
  showSecondaryTitle: boolean;
  toggleShowSecondaryTitle(): void;
  showSubtitle: boolean;
  toggleShowSubtitle(): void;
  showNumberFormattingThousands: boolean;
  toggleShowNumberFormattingThousands(): void;
  showAppliedFilters: boolean;
  toggleShowAppliedFilters(): void;
  appliedFilters: number;
  currencySymbolDisplay: ECurrencySymbolDisplay;
  setCurrencySymbolDisplay: (display: ECurrencySymbolDisplay) => void;
}

export const ExportPdfHeaderControllers: React.FC<IExportPdfHeaderControllersProps> = ({
  localeText,
  showHeader,
  toggleShowHeader,
  showPageSetting,
  setShowPageSetting,
  primaryTitle,
  secondaryTitle,
  subtitle,
  setPrimaryTitle,
  setSecondaryTitle,
  setSubtitle,
  showPrimaryTitle,
  toggleShowPrimaryTitle,
  showSecondaryTitle,
  toggleShowSecondaryTitle,
  showSubtitle,
  toggleShowSubtitle,
  showNumberFormattingThousands,
  toggleShowNumberFormattingThousands,
  showAppliedFilters,
  toggleShowAppliedFilters: setShowAppliedFilters,
  appliedFilters,
  currencySymbolDisplay,
  setCurrencySymbolDisplay
}) => {
  const { headerSection } = localeText.exportPdfModal;
  const maxPrimaryTitleLength = 100;
  const maxSubtitleLength = 100;
  const maxSecondaryTitleLength = 60;

  const onPrimaryTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.trim().length > maxPrimaryTitleLength) return;
    setPrimaryTitle(e.target.value);
  };

  const onSecondaryTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.trim().length > maxSecondaryTitleLength) return;
    setSecondaryTitle(e.target.value);
  };

  const onSubtitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.trim().length > maxSubtitleLength) return;
    setSubtitle(e.target.value);
  };

  return (
    <FormControl fullWidth sx={styles.formControl}>
      <FormLabel id="header-title-label" sx={styles.formLabel}>
        <Typography variant="h4" component="label" sx={styles.typographyBase}>
          {headerSection.headerTitle}
        </Typography>
        <Switch
          size="small"
          aria-labelledby="header-title-label"
          checked={showHeader}
          onChange={toggleShowHeader}
          data-test="toggle-header"
        />
      </FormLabel>

      {showHeader && (
        <Box sx={styles.formContent}>
          <TextField
            size="medium"
            select
            label={headerSection.showPageSettings}
            value={showPageSetting}
            onChange={(e) => {
              setShowPageSetting(e.target.value as EShowPageSetting);
            }}
            fullWidth
            data-test="page-orientation-dropdown"
          >
            <MenuItem value={EShowPageSetting.onAllPages} data-test="page-orientation-option-portrait">
              {headerSection.showOnAllPageSetting}
            </MenuItem>
            <MenuItem value={EShowPageSetting.onlyOnTheFirstPage} data-test="page-orientation-option-landscape">
              {headerSection.showOnlyOnFirstPageSetting}
            </MenuItem>
          </TextField>

          <FormControlLabel
            sx={styles.textFieldControlLabel}
            control={
              <TextField
                size="medium"
                value={primaryTitle}
                onChange={onPrimaryTitleChange}
                disabled={!showPrimaryTitle}
                multiline
                fullWidth
                inputProps={{ maxLength: maxPrimaryTitleLength }}
                data-test="primary-title-input"
              />
            }
            label={
              <Box className="cds-textFieldLabelWithCounter" sx={styles.textFieldControlLabel}>
                <Checkbox
                  size="medium"
                  checked={showPrimaryTitle}
                  onChange={toggleShowPrimaryTitle}
                  sx={styles.formControlCheckbox}
                />
                <Typography variant="h4" component="span" sx={styles.typographyBase} data-test="primary-title-label">
                  {headerSection.primaryTitle}
                </Typography>
                <Typography variant="bodySmall" sx={styles.typographyCounter}>
                  {primaryTitle.length}/{maxPrimaryTitleLength}
                </Typography>
              </Box>
            }
            labelPlacement="top"
          />

          <FormControlLabel
            sx={styles.textFieldControlLabel}
            control={
              <TextField
                size="medium"
                value={secondaryTitle}
                onChange={onSecondaryTitleChange}
                disabled={!showSecondaryTitle}
                multiline
                fullWidth
                inputProps={{ maxLength: maxSecondaryTitleLength }}
                data-test="secondary-title-input"
              />
            }
            label={
              <Box className="cds-textFieldLabelWithCounter">
                <Checkbox
                  size="medium"
                  checked={showSecondaryTitle}
                  onChange={toggleShowSecondaryTitle}
                  sx={styles.formControlCheckbox}
                />
                <Typography variant="h4" component="span" sx={styles.typographyBase} data-test="secondary-title-label">
                  {headerSection.secondaryTitle}
                </Typography>
                <Typography variant="bodySmall" sx={styles.typographyCounter}>
                  {secondaryTitle.length}/{maxSecondaryTitleLength}
                </Typography>
              </Box>
            }
            labelPlacement="top"
          />

          <FormControlLabel
            sx={styles.textFieldControlLabel}
            control={
              <TextField
                size="medium"
                value={subtitle}
                onChange={onSubtitleChange}
                disabled={!showSubtitle}
                multiline
                fullWidth
                inputProps={{ maxLength: maxSubtitleLength }}
                data-test="subtitle-title-input"
              />
            }
            label={
              <Box className="cds-textFieldLabelWithCounter">
                <Checkbox
                  size="medium"
                  checked={showSubtitle}
                  onChange={toggleShowSubtitle}
                  data-test="subtitle-checkbox"
                  aria-label={headerSection.subtitle}
                  name="subtitle-checkbox"
                  sx={styles.formControlCheckbox}
                />
                <Typography variant="h4" component="span" sx={styles.typographyBase} data-test="subtitle-label">
                  {headerSection.subtitle}
                </Typography>
                <Typography variant="bodySmall" sx={styles.typographyCounter}>
                  {subtitle.length}/{maxSubtitleLength}
                </Typography>
              </Box>
            }
            labelPlacement="top"
          />

          <FormControl component="fieldset" sx={styles.numberFormattingFieldset}>
            <FormLabel component="legend" data-test="numberFormatting-title" sx={styles.formLabelLegend}>
              <Typography variant="h4" component="label" sx={styles.typographyBase}>
                {headerSection.numberFormattingTitle}
              </Typography>
            </FormLabel>
            <FormControlLabel
              label={
                <Typography variant="bodyDefault" sx={styles.typographyBase}>
                  {headerSection.numberFormattingThousands}
                </Typography>
              }
              control={
                <Checkbox
                  size="medium"
                  onChange={toggleShowNumberFormattingThousands}
                  checked={showNumberFormattingThousands}
                />
              }
              value={showNumberFormattingThousands}
              sx={styles.formControlLabelCheckbox}
              data-test="number-formatting-checkbox"
            />
            <TextField
              size="medium"
              select
              label={headerSection.currencySymbolLabel}
              value={currencySymbolDisplay}
              onChange={(e) => {
                setCurrencySymbolDisplay(e.target.value as ECurrencySymbolDisplay);
              }}
              fullWidth
              data-test="currency-symbol-dropdown"
            >
              <MenuItem value={ECurrencySymbolDisplay.All}>{headerSection.currencySymbolOptions.all}</MenuItem>
              <MenuItem value={ECurrencySymbolDisplay.None}>{headerSection.currencySymbolOptions.none}</MenuItem>
              <MenuItem value={ECurrencySymbolDisplay.FirstLastPerGroup}>
                {headerSection.currencySymbolOptions.firstLastPerGroup}
              </MenuItem>
            </TextField>
          </FormControl>

          {appliedFilters ? (
            <FormControl component="fieldset" sx={styles.filtersFormControl}>
              <FormLabel component="legend" data-test="applied-filters-title" sx={styles.formLabelLegend}>
                <Typography variant="h5" component="label" sx={styles.typographyBase}>
                  {headerSection.appliedFilters}
                </Typography>
              </FormLabel>
              <FormControlLabel
                label={
                  <Typography variant="bodySmall" sx={styles.typographyBase}>
                    {`${headerSection.showLabel} ${appliedFilters} ${headerSection.appliedLabel} ${
                      appliedFilters > 1 ? headerSection.filtersLabel : headerSection.filterLabel
                    }`}
                  </Typography>
                }
                control={<Checkbox size="small" checked={showAppliedFilters} onChange={setShowAppliedFilters} />}
                sx={styles.formControlLabelCheckbox}
              />
            </FormControl>
          ) : null}
        </Box>
      )}
    </FormControl>
  );
};
